import { SetMirrorData } from '@type/job-add';
import { useTranslation } from 'next-i18next';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Button, ListboxWrapper } from '@components/common/Forms';
import {
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { RecordInterface } from '@type/form-types';
import { generateNumbersArray } from '@utils/arrays/generateNumbersArray';
import { dataTestId } from '@services/data-test-id';

/** Component props */
interface RecruitmentObject { label: string; value: number; }
interface RepostingNumberProps {
  record?: RecordInterface;
  setRepostingNumber?: Dispatch<SetStateAction<number>>;
  initialRepostingIndex?: number;
  setMirrorData?: SetMirrorData;
  countFromOne?: boolean;
  removeFromSubmit?: boolean;
}

/**
 * @description Reposting number component
 * - `record`: The Bj form record function.
 * - `setRepostingNumber`: The set reposting number function. Use it to pass the selected number to the parent component.
 * - `initialRepostingIndex`: The initial reposting index. Default is 0 (no extension).
 * - `setMirrorData`: The set mirror data function.
 * - `countFromOne`: If the reposting number should start from 1. Because API...
 * - `removeFromSubmit`: If the reposting number should be omitted from the form submit.
 *
 * **IMPORTANT**: Back-end counts from 1 on job activation and from 0 on job upgrade (in active period).
 */
export const RepostingNumber = (props: RepostingNumberProps) => {
  // Destructure props
  const {
    record,
    setRepostingNumber,
    initialRepostingIndex = 0,
    setMirrorData,
    countFromOne = false,
    removeFromSubmit = false,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Make recruitment periods
  const recruitmentPeriods: RecruitmentObject[] = useMemo(() => generateNumbersArray(countFromOne ? 1 : 0, 8).map((number) => ({
    label: `${(countFromOne ? number - 1 : number) * 30} ${t('global.period.days')}`,
    value: number,
  })), [t, countFromOne]);

  // Classes
  const listBoxOption = 'min-w-max cursor-pointer py-1.5 px-4 text-sm text-ink-medium ui-selected:text-ink ui-selected:font-bold ui-active:bg-surface-50 ui-active:text-ink';

  // Render component
  // ************************
  return (
    <ListboxWrapper
      recordFunc={record}
      recordOptions={{
        convertValue: 'toNumber',
        ...(removeFromSubmit && { removeFromSubmit: true }),
      }}
      fieldName="repostingNumber"
      data={recruitmentPeriods}
      defaultValueKey="value"
      initialSelectedIndex={[initialRepostingIndex]}
      withTranslation
      labelKey="label"
      renderListbox={({
        lsData,
        selected,
        setSelected,
        selectedLabel,
      }) => {
        const selectedItem = selected as RecruitmentObject;
        const noExtension = countFromOne ? selectedItem.value === 1 : selectedItem.value === 0;
        const buttonBorderColor = noExtension ? 'border-input' : 'border-secondary ring-2 ring-secondary/30';
        const buttonLabel = noExtension ? t('job.add.reposting.label') : `${t('job.add.reposting.label')}: ${selectedLabel}`;

        return (
          <ListboxStyled
            className="relative"
            by="value"
            value={selected}
            onChange={(item) => {
              setSelected(item as RecruitmentObject);
              setRepostingNumber?.((item as RecruitmentObject).value);
              setMirrorData?.({ repostingNumber: (item as RecruitmentObject).value });
            }}
          >
            <ListboxStyledButton
              as={Button}
              styling="none"
              className={`flex items-center gap-2 rounded border px-4 py-2 ${buttonBorderColor}`}
              {...dataTestId('jobAddPage.jobActions.promoExtendButton')}
            >
              {buttonLabel}
              <ChevronUpDownIcon className="-mr-2 size-5 shrink-0 text-ink/80" />
            </ListboxStyledButton>

            <ListboxStyledOptions
              className="scrollbar-thin mt-0.5 max-h-40 min-w-full overflow-y-auto py-1"
              animation="slideDown"
              position="right"
              {...dataTestId('jobAddPage.jobActions.promoExtendList')}
            >
              {lsData.map((field) => (
                <ListboxStyledOption
                  key={field.value}
                  value={field}
                  className={listBoxOption}
                >
                  {field.label}
                </ListboxStyledOption>
              ))}
            </ListboxStyledOptions>
          </ListboxStyled>
        );
      }}
    />
  );
};
